import {
  builtinTypes,
  equalsType,
  getFriendlyTypeName,
  isBoolType,
  isDateType,
  isListType,
  isNumberType,
  isStringType,
  prune,
  type RynoListType,
  type RynoType,
  typeBuilder,
} from '@taxfyle/ryno'
import type { ValueTypeResource } from '@/features/legends/api/resources/engine'
import {
  UnknownElementError,
  type ValueType,
} from '@/features/legends/editor/legend-document'

export type { RynoType, RynoListType }

/**
 * Ryno types and helpers, as the legend builder uses them.
 */
export const Types = {
  nothing: builtinTypes.nothing,
  number: builtinTypes.number,
  string: builtinTypes.string,
  boolean: builtinTypes.boolean,
  date: builtinTypes.date,
  list: (elementType: RynoType): RynoListType => typeBuilder.list(elementType),
  isListType,
  isStringType,
  isBoolType,
  isNumberType,
  getFriendlyTypeName,
  describeType,
  fromJS: typeFromJS,
  toJS: typeToJS,
  equalsType: (left: RynoType | null, right: RynoType | null) =>
    !!left && !!right && equalsType(prune(left), prune(right)),
}

/**
 * The type's name as completions show it, like "List of String".
 *
 * @param type
 */
function describeType(type: RynoType): string {
  const common = commonTypeNames.get(type)
  if (common) {
    return common
  }
  if (isListType(type)) {
    return `List of ${describeType(type.elementType)}`
  }
  return getFriendlyTypeName(type)
}

/**
 * Converts a value type to a Ryno type. Throws for one the builder doesn't
 * know: typing it as `any` would silently disable type checking.
 *
 * @param valueType
 */
function typeFromJS(valueType: ValueType): RynoType {
  switch (valueType.type) {
    case 'Number':
      return Types.number
    case 'String':
      return Types.string
    case 'Date':
      return Types.date
    case 'Boolean':
      return Types.boolean
    case 'List':
      return Types.list(typeFromJS(valueType.itemType))
    case 'Unknown':
      throw new UnknownElementError('value', valueType)
  }
}

/**
 * Converts a personal organizer binding's value type to a Ryno type. The
 * organizer has no dates, so a date is as unknown as a type the builder
 * doesn't know.
 *
 * @param valueType
 */
export function typeFromValueType(valueType: ValueTypeResource): RynoType {
  switch (valueType.type) {
    case 'Number':
      return Types.number
    case 'String':
      return Types.string
    case 'Boolean':
      return Types.boolean
    case 'List':
      return Types.list(typeFromValueType(valueType.itemType))
    case 'Date':
      break
  }
  throw new Error(`Unknown value type "${String(valueType.type)}"`)
}

/**
 * Converts a Ryno type to a value type. Only the types a variable can have
 * convert.
 *
 * @param type
 */
function typeToJS(type: RynoType): ValueType {
  if (isListType(type)) {
    return { type: 'List', itemType: typeToJS(type.elementType) }
  }
  if (isNumberType(type)) {
    return { type: 'Number' }
  }
  if (isStringType(type)) {
    return { type: 'String' }
  }
  if (isBoolType(type)) {
    return { type: 'Boolean' }
  }
  if (isDateType(type)) {
    return { type: 'Date' }
  }
  throw new Error(`No value type for "${getFriendlyTypeName(type)}"`)
}

const commonTypeNames = new Map<RynoType, string>([
  [Types.string, 'String'],
  [Types.number, 'Number'],
  [Types.boolean, 'Boolean'],
  [Types.date, 'Date + Time'],
])
